import type { CaseStudy } from '../types';

export const amazon: CaseStudy = {
  id: 'amazon',
  order: 3,
  title: '亚马逊',
  english: 'Amazon',
  category: 'platform',
  era: '1994 — 今',
  region: '美国 / 全球',
  accent: '#516f9f',
  hook: '它从卖书开始，最后卖的是「让别人在自己修的路上卖东西」的能力：流量、履约、云和广告。',
  question: '一个长期低利润的零售商，如何同时养活庞大的物流网络和高利润的云业务？',
  summary:
    '亚马逊把零售需求、第三方卖家、物流密度和云计算叠成一个互相喂养的系统。飞轮不是一句口号，而是一组需要持续资本投入和治理选择的反馈回路。',
  thesis: '平台的力量来自「自己修基础设施，再让别人的交易跑在上面」；但这也会把裁判与运动员放进同一个身体。',
  quote: '飞轮真正转动的条件，是每一圈都能给下一圈带来可测量的成本或体验优势。',
  entryNodeIds: ['amazon-flywheel', 'amazon-logistics', 'amazon-marketplace'],
  focusNodeId: 'amazon-flywheel',
  lensIds: ['network', 'scale', 'incentives', 'lockin'],
  opportunityIds: ['opp-seller-profit', 'opp-multichannel-compliance'],
  sourceIds: ['amazon-ar-2024', 'ftc-amazon-2023'],
  facts: [
    {
      text: '亚马逊 1994 年以在线书店起家，2024 年净销售额约 6,380 亿美元。',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      text: '2024 年 AWS 净销售额约 1,076 亿美元，营业利润约 398 亿美元；公司总营业利润约 686 亿美元。',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      text: '美国联邦贸易委员会（FTC）在 2023 年起诉亚马逊，指控其非法维持垄断力量；这些是监管机构的主张，案件结果需以法院进展为准。',
      sourceIds: ['ftc-amazon-2023'],
    },
  ],
  nodes: [
    {
      id: 'amazon',
      caseId: 'amazon',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '亚马逊',
      summary: '从在线书店到交易、物流与云的基础设施。',
      detail:
        '亚马逊的起点是零售，但它的护城河逐渐从「卖什么」转向「交易如何发生」：Prime 会员、第三方卖家、仓储与配送、广告、AWS。它同时是基础设施提供者、规则制定者和市场竞争者，这让效率优势与利益冲突一起增长。',
      boundary: '这套模型需要巨大的持续资本投入和成熟资本市场支持，不是轻资产平台的自然结果。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      id: 'amazon-flywheel',
      caseId: 'amazon',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '飞轮：低价 → 流量 → 供给',
      summary: '更低价格吸引更多顾客，更多顾客吸引更多卖家，卖家又扩大选择。',
      detail:
        '飞轮的核心是反馈回路：选择多、价格低、配送快，提高顾客留存；顾客规模又让卖家愿意入驻；卖家增加选择与广告收入，进一步支持基础设施投入。它不是自动成立的，需要每一环都能被测量和改善。',
      boundary: '当获客成本、履约成本或卖家费用上升快于留存收益，飞轮会变成昂贵的补贴循环。',
      counter: '许多垂直电商靠专业选品和社区信任活得很好，并不需要追求全品类飞轮。',
      evidence: 'mechanism',
      confidence: 'medium',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      id: 'amazon-logistics',
      caseId: 'amazon',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '物流密度：固定网络被更多包裹摊薄',
      summary: '仓库、分拣和配送路线越密，单件履约成本越有下降空间。',
      detail:
        '履约网络是典型的重资产固定成本。订单密度上升，仓库利用率、车辆装载率和最后一公里路线效率都会改善。但密度有边界：偏远地区、旺季峰值和退货率会迅速推高成本。',
      boundary: '当订单密度不足、劳动力紧张或峰值波动过大，物流会从护城河变成利润黑洞。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      id: 'amazon-marketplace',
      caseId: 'amazon',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '第三方卖家：扩张选择，也制造裁判冲突',
      summary: '开放平台让供给快速扩张，但平台同时卖自有商品并制定规则。',
      detail:
        '第三方卖家扩大了商品选择，平台收取佣金、广告和物流服务费。问题在于：平台既是规则制定者，又是竞争者，还能看到卖家数据。即使没有滥用，这种结构也会让卖家对费用、排名和自有品牌竞争保持警惕。',
      boundary: '治理透明、数据隔离和可申诉机制可以缓解冲突，但很难彻底消除结构性利益冲突。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['amazon-ar-2024', 'ftc-amazon-2023'],
    },
    {
      id: 'amazon-aws',
      caseId: 'amazon',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: 'AWS：把固定 IT 成本变成可变服务',
      summary: '企业不必自建机房，按需购买算力；规模效应与转换成本同时存在。',
      detail:
        '云计算把服务器、存储和数据库的固定资本开支，变成按使用量付费的运营支出。对客户，降低了试错门槛；对提供商，规模越大，单位算力成本越低，同时客户迁移数据和应用的转换成本上升。',
      boundary: '价格战、监管、客户自建或多云策略会削弱锁定；高利润也会吸引更强竞争。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      id: 'amazon-condition',
      caseId: 'amazon',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：资本耐心与消费基础设施',
      summary: '长期低利润扩张、信用卡支付、成熟物流与资本市场，共同支撑这套模型。',
      detail:
        '亚马逊可以多年把利润投入扩张，部分原因是资本市场愿意为增长和基础设施付费。没有便捷支付、可靠物流和高消费密度，飞轮的转速会大幅下降。',
      boundary: '利率上升、投资者偏好改变或消费下沉，都会让「先规模后利润」的模式承压。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      id: 'amazon-outcome',
      caseId: 'amazon',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：交易基础设施与利润池分离',
      summary: '零售提供规模与数据，云和广告提供更高利润，二者互相支撑。',
      detail:
        '亚马逊的利润并不平均分布在所有业务：零售履约薄利甚至亏损，云与广告贡献更高利润。理解这一点，比笼统说「亚马逊很赚钱」更有用——它靠一个业务养基础设施，再用基础设施服务更多业务。',
      boundary: '利润池会随竞争、监管和投资周期变化；云与广告的高利润不是永久自然规律。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['amazon-ar-2024'],
    },
    {
      id: 'amazon-risk',
      caseId: 'amazon',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：裁判与运动员合一',
      summary: '平台越成为基础设施，规则公平、数据使用与反垄断压力越大。',
      detail:
        '当平台同时掌握流量分配、卖家数据、广告竞价和自有品牌，任何排名变化都可能被解读为偏袒。监管与舆论压力会提高合规成本，也可能限制并购、独家协议或自我优待。',
      boundary: '如果市场仍能多归属、卖家能低成本迁移，冲突的实际伤害可能小于结构性担忧。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['ftc-amazon-2023'],
    },
    {
      id: 'amazon-opp',
      caseId: 'amazon',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：卖「平台上的利润」',
      summary: '卖家不缺开店工具，缺的是把费用、广告、退货和现金流算清楚的决策能力。',
      detail:
        '平台越复杂，卖家越需要利润诊断、合规、税务、广告归因和多渠道运营。机会不在于再做一个更大的平台，而在于帮助小卖家在平台规则下保住利润、降低单点依赖。',
      boundary: '工具容易同质化，必须绑定真实决策与结果；只做数据看板很难收费。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['amazon-ar-2024'],
    },
  ],
  edges: [
    {
      id: 'amazon-e1',
      caseId: 'amazon',
      from: 'amazon',
      to: 'amazon-flywheel',
      label: '从零售到回路',
      explanation: '更低价格和更多选择提高留存，留存又吸引卖家与投资。',
      tone: 'cause',
    },
    {
      id: 'amazon-e2',
      caseId: 'amazon',
      from: 'amazon',
      to: 'amazon-logistics',
      label: '订单喂养网络',
      explanation: '订单密度让仓储与配送的固定成本被更多包裹分摊。',
      tone: 'cause',
    },
    {
      id: 'amazon-e3',
      caseId: 'amazon',
      from: 'amazon-flywheel',
      to: 'amazon-marketplace',
      label: '流量吸引供给',
      explanation: '顾客规模让第三方卖家愿意入驻，卖家又扩大选择与广告收入。',
      tone: 'cause',
    },
    {
      id: 'amazon-e4',
      caseId: 'amazon',
      from: 'amazon-logistics',
      to: 'amazon-marketplace',
      label: '履约成为卖点',
      explanation: '快速配送提高买家预期，也让卖家更依赖平台物流服务。',
      tone: 'enable',
    },
    {
      id: 'amazon-e5',
      caseId: 'amazon',
      from: 'amazon-logistics',
      to: 'amazon-aws',
      label: '基础设施能力外溢',
      explanation: '大规模计算与运营能力，最终可以产品化，卖给外部企业。',
      tone: 'cause',
    },
    {
      id: 'amazon-e6',
      caseId: 'amazon',
      from: 'amazon-marketplace',
      to: 'amazon-outcome',
      label: '供给扩张',
      explanation: '第三方卖家扩大选择，平台获得佣金、广告与物流收入。',
      tone: 'cause',
    },
    {
      id: 'amazon-e7',
      caseId: 'amazon',
      from: 'amazon-aws',
      to: 'amazon-outcome',
      label: '高利润池',
      explanation: '云业务贡献更高利润，为集团提供再投资能力与估值支撑。',
      tone: 'cause',
    },
    {
      id: 'amazon-e8',
      caseId: 'amazon',
      from: 'amazon-condition',
      to: 'amazon-outcome',
      label: '资本与消费前提',
      explanation: '长期资本耐心、支付与物流基础设施，让低利润扩张可以持续。',
      tone: 'enable',
    },
    {
      id: 'amazon-e9',
      caseId: 'amazon',
      from: 'amazon-outcome',
      to: 'amazon-risk',
      label: '基础设施即权力',
      explanation: '当平台控制交易入口、数据和履约，竞争与治理问题会随之放大。',
      tone: 'risk',
    },
    {
      id: 'amazon-e10',
      caseId: 'amazon',
      from: 'amazon-risk',
      to: 'amazon-opp',
      label: '复杂性产生服务需求',
      explanation: '规则越复杂，卖家越需要利润诊断、合规与多渠道能力。',
      tone: 'enable',
    },
  ],
};
